import React, { useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Progress,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { authService } from "../../api/auth";
import { useAuth } from "../../hooks/useAuth";

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Derive email from navigation state or localStorage; do not re-ask the user
  const initialEmail = (() => {
    const stateEmail = (location && (location as any).state && (location as any).state.email) || "";
    if (stateEmail) return stateEmail as string;
    try { return localStorage.getItem('reset_email') || ""; } catch { /* noop */ }
    if (user?.email) return user.email;
    return "";
  })();

  const [email] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const formBg = useColorModeValue("var(--color-card-bg)", "var(--color-card-bg)");
  const borderColor = useColorModeValue("var(--color-border)", "var(--color-border)");
  const subtitleColor = useColorModeValue("var(--color-text-muted)", "var(--color-text-muted)");

  const calculatePasswordStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
    return Math.min(strength, 100);
  };

  const passwordStrength = calculatePasswordStrength();
  const getStrengthColor = () => {
    if (passwordStrength < 40) return "red";
    if (passwordStrength < 70) return "yellow";
    return "green";
  };

  const passwordRequirements = [
    { met: password.length >= 8, text: "At least 8 characters" },
    { met: /[a-z]/.test(password), text: "Contains lowercase letter" },
    { met: /[A-Z]/.test(password), text: "Contains uppercase letter" },
    { met: /[0-9]/.test(password), text: "Contains number" },
    { met: /[^a-zA-Z0-9]/.test(password), text: "Contains special character" },
  ];

  const isCodeValidFormat = () => /^\d{6}$/.test(code.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("We couldn't determine your email. Please go back and request a new code.");
      return;
    }
    if (!isCodeValidFormat()) {
      setError("Please enter the 6-digit code sent to your email");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(
        email,
        code.trim(),
        password,
        confirmPassword
      );
      try { localStorage.removeItem('reset_email'); } catch {}
      navigate("/login?reset=success");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to reset password. The code may be invalid or expired.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      maxW="md"
      w="full"
      bg={formBg}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={8}
      boxShadow="lg"
    >
      <VStack spacing={6} as="form" onSubmit={handleSubmit}>
        <VStack spacing={2}>
          <Heading size="lg" fontWeight="bold">
            Reset Your Password
          </Heading>
          <Text fontSize="sm" color={subtitleColor} textAlign="center">
            Enter the 6-digit code sent to your email (expires in 15 minutes), then set a new password
          </Text>
          {email && (
            <Text fontSize="sm" color={subtitleColor} textAlign="center">
              Email: <strong>{email}</strong>
            </Text>
          )}
        </VStack>

        {/* Show success notice if navigated from Forgot Password */}
        {((location && (location as any).state && (location as any).state.notice) === 'reset_sent') && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              If an account exists with this email, a reset code was sent. It expires in 15 minutes.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!email && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              We need your account email to reset your password. Please
              <RouterLink to="/forgot-password" style={{ color: 'var(--color-primary)', marginLeft: 4 }}> request a new code</RouterLink>.
            </AlertDescription>
          </Alert>
        )}

        <FormControl isRequired>
          <FormLabel>Reset Code</FormLabel>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="Enter code (e.g., 123456)"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoFocus
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>New Password</FormLabel>
          <InputGroup>
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            <InputRightElement>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <ViewOffIcon /> : <ViewIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>

        {password && (
          <Box w="full">
            <Text fontSize="sm" color={subtitleColor} mb={2}>
              Password Strength
            </Text>
            <Progress
              value={passwordStrength}
              colorScheme={getStrengthColor()}
              size="sm"
              borderRadius="md"
              mb={3}
            />
            <List spacing={1}>
              {passwordRequirements.map((req, index) => (
                <ListItem key={index} fontSize="sm" color={req.met ? "green.500" : subtitleColor}>
                  <ListIcon
                    as={req.met ? CheckCircleIcon : WarningIcon}
                    color={req.met ? "green.500" : "gray.400"}
                  />
                  {req.text}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <FormControl isRequired>
          <FormLabel>Confirm New Password</FormLabel>
          <InputGroup>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            <InputRightElement>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>

        {confirmPassword && password !== confirmPassword && (
          <Text fontSize="sm" color="red.500">
            Passwords do not match
          </Text>
        )}

        <Button
          type="submit"
          colorScheme="blue"
          size="lg"
          w="full"
          isLoading={isLoading}
          loadingText="Resetting..."
          isDisabled={!email || !isCodeValidFormat() || !password || !confirmPassword || password !== confirmPassword || isLoading}
        >
          Reset Password
        </Button>

        <Text fontSize="sm" color={subtitleColor}>
          Remember your password?{" "}
          <RouterLink to="/login" style={{ color: "var(--color-primary)" }}>
            Back to Login
          </RouterLink>
        </Text>
      </VStack>
    </Box>
  );
};

export default ResetPasswordForm;
